import { Injectable } from '@angular/core';

import { AdditionalInformation, AdditionalInformationValueType, EdsPromoTokenType, KeyValueInformation, PromotionTokenType } from '@bpos';
import { EdsPromoToken, PromoToken, TokenPayoutType } from '@bpos/common/bet-placement';
import { RewardTargetType } from '@bpos/v1/sports-promo/tokens';
import { CrmConfig } from '@frontend/sports/common/client-config-data-access';
import { round } from 'lodash-es';
import { PayoutType, RewardTokenType } from 'packages/sports/web/app/src/tokens-base/token-base.models';
import { TokensCalculatorService } from 'packages/sports/web/app/src/tokens-base/tokens-calculator.service';

import {
    BetAndGetEdsToken,
    BetAndGetToken,
    IRewardToken,
    IncrementalPayoutDetails,
    OddsBoostToken,
    PromoTokenPlaceBetSignPostingInfo,
    RewardTokenAdditionalInfoKeys,
    RiskFreeToken,
} from '../reward-tokens.model';
import {
    getAccaBoostRatio,
    isAccaBoostToken,
    isFreebetToken,
    isIncrementalPayoutEligible,
    isOddsBoostToken,
    isRiskFreeToken,
} from './reward-tokens.utils';

@Injectable({
    providedIn: 'root',
})
export class RewardTokensMapper {
    private readonly PRECISION = 2;

    constructor(
        private tokensConfig: CrmConfig,
        private tokensCalculator: TokensCalculatorService,
    ) {}

    getPlacementModel(token: IRewardToken, winningsBoost: number | null, pickCount: number | null, stake?: number): PromoToken {
        const result: PromoToken = {
            tokenId: token.id,
            userTokenId: token.userTokenId,
            tokenType: this.mapTokenType(token.rewardTokenType),
            payoutType: this.mapPayoutType(token.payoutType),
            additionalInformation: {
                informationItems: [],
            },
        };

        if (isFreebetToken(token)) {
            // This is done on purpose
            // Without this, bet placement processing will fail (most likely related to serialization)
            result.tokenId = undefined!;
            result.payoutType = undefined;
            result.additionalInformation.informationItems = [
                {
                    key: RewardTokenAdditionalInfoKeys.IsDigitalV2,
                    value: this.tokensConfig.tokens.isDigitalV2Freebet.toString(),
                    valueType: AdditionalInformationValueType.Boolean,
                },
            ];
        } else if (isOddsBoostToken(token)) {
            result.additionalInformation.informationItems = this.getOddsBoostAdditionalInfo(winningsBoost, token);
        } else if (isRiskFreeToken(token)) {
            result.additionalInformation.informationItems = this.getRiskFreeAdditionalInfo(token);
        } else if (isAccaBoostToken(token)) {
            const additionalInfo: KeyValueInformation[] = [
                {
                    key: RewardTokenAdditionalInfoKeys.AccaBoostRatio,
                    value: getAccaBoostRatio(token, pickCount!).toString(),
                    valueType: AdditionalInformationValueType.Decimal,
                },
                {
                    key: RewardTokenAdditionalInfoKeys.MaxWinningsBoost,
                    value: token.maxReward.toString(),
                    valueType: AdditionalInformationValueType.Decimal,
                },
                {
                    key: RewardTokenAdditionalInfoKeys.UserExpectedWinningsBoost,
                    value: winningsBoost?.toString() ?? '',
                    valueType: AdditionalInformationValueType.Decimal,
                },
            ];

            result.additionalInformation.informationItems = additionalInfo;
        }

        if (isRiskFreeToken(token)) {
            const payout = this.tokensCalculator.getTokenPercentageReturns(token.maximumPayout, token.riskFreePercentage, stake);
            const incrementalDetails = this.getIncrementalDetails(token, payout, stake);

            if (token.rewardTargetType === RewardTargetType.WelcomeOffer || incrementalDetails) {
                const placeBetSignPostingInfo: PromoTokenPlaceBetSignPostingInfo = {
                    rewardTargetType: token.rewardTargetType,
                    noOfSlabs: incrementalDetails?.noOfSlabs,
                    slabAmount: incrementalDetails?.slabAmount,
                };

                result.additionalInformation.informationItems = [
                    ...result.additionalInformation.informationItems,
                    ...this.addPlacebetSignPostDetails(placeBetSignPostingInfo),
                ];
            }
        }

        return result;
    }

    private getOddsBoostAdditionalInfo(winningsBoost: number | null, token: OddsBoostToken): KeyValueInformation[] {
        const additionalInfo: KeyValueInformation[] = [
            {
                key: RewardTokenAdditionalInfoKeys.UserExpectedWinningsBoost,
                value: winningsBoost?.toString() ?? '',
                valueType: AdditionalInformationValueType.Decimal,
            },
            {
                key: RewardTokenAdditionalInfoKeys.MaxTokenStake,
                value: token.maximumStake?.toString() ?? '',
                valueType: AdditionalInformationValueType.Decimal,
            },
            {
                key: RewardTokenAdditionalInfoKeys.MaxWinningsBoost,
                value: token.maxWinningsBoost?.toString() ?? '',
                valueType: AdditionalInformationValueType.Decimal,
            },
            {
                key: RewardTokenAdditionalInfoKeys.BoostPercentage,
                value: token.boostFactor.toString(),
                valueType: AdditionalInformationValueType.Int32,
            },
            {
                key: RewardTokenAdditionalInfoKeys.OddsBoostFormulaType,
                value: token.oddsBoostFormula.toString(),
                valueType: AdditionalInformationValueType.String,
            },
        ];

        return additionalInfo;
    }

    private getRiskFreeAdditionalInfo(token: RiskFreeToken): KeyValueInformation[] {
        const additionalInfo: KeyValueInformation[] = [
            {
                key: RewardTokenAdditionalInfoKeys.RiskFreeMaxCompensation,
                value: token.maximumPayout.toString(),
                valueType: AdditionalInformationValueType.Decimal,
            },
            {
                key: RewardTokenAdditionalInfoKeys.RiskFreePercentage,
                value: token.riskFreePercentage.toString(),
                valueType: AdditionalInformationValueType.Int32,
            },
        ];

        return additionalInfo;
    }

    mapPayoutType(clientPayoutType: PayoutType): TokenPayoutType {
        switch (clientPayoutType) {
            case PayoutType.FreeBet:
                return TokenPayoutType.FreeBet;
            case PayoutType.Bonus:
                return TokenPayoutType.Bonus;
            case PayoutType.Cash:
                return TokenPayoutType.Cash;
        }

        // should be none for bet station
        return TokenPayoutType.None;
    }

    mapTokenType(clientTokenType: RewardTokenType): PromotionTokenType {
        switch (clientTokenType) {
            case RewardTokenType.FreeBet:
                return PromotionTokenType.FreeBet;
            case RewardTokenType.BetStationFreebet:
                return PromotionTokenType.FreeBet;
            case RewardTokenType.OddsBoost:
                return PromotionTokenType.OddsBoost;
            case RewardTokenType.RiskFreeBet:
                return PromotionTokenType.RiskFree;
            case RewardTokenType.AccaBoost:
                return PromotionTokenType.AccaBoost;
            case RewardTokenType.BestOddsGuaranteed:
                return PromotionTokenType.BestOdds;
            default:
                return PromotionTokenType.None;
        }
    }

    private addBetAndGetTokenInfo(token: BetAndGetToken, stake: number): AdditionalInformation {
        const additionalInformation: AdditionalInformation = {
            informationItems: [],
        };

        const incrementalDetails = this.getIncrementalDetails(token, token.payoutValue, stake);

        const placeBetSignPostingData: BetAndGetEdsToken = {
            payoutType: token.payoutType,
            triggerType: token.rewardTriggerType,
            rewardTargetType: token.rewardTargetType,
            payoutDetails: {
                value: token.payoutValue,
                percentage: token.percentagePayout,
                maxValue: token.maximumPayout,
            },
        };

        if (incrementalDetails) {
            placeBetSignPostingData.payoutDetails = {
                ...placeBetSignPostingData.payoutDetails,
                noOfSlabs: incrementalDetails.noOfSlabs,
                slabAmount: incrementalDetails.slabAmount,
            };
        }

        additionalInformation.informationItems = this.addPlacebetSignPostDetails(placeBetSignPostingData);

        return additionalInformation;
    }

    createEDSPromoTokens(selectedToken: BetAndGetToken, stake: number): EdsPromoToken {
        return {
            tokenType: EdsPromoTokenType.BetAndGet,
            tokenId: selectedToken.id,
            userTokenId: selectedToken.userTokenId,
            additionalInformation: this.addBetAndGetTokenInfo(selectedToken, stake),
        };
    }

    private addPlacebetSignPostDetails(placeBetSignPostingData: BetAndGetEdsToken | PromoTokenPlaceBetSignPostingInfo): KeyValueInformation[] {
        const additionalInfo: KeyValueInformation[] = [
            {
                key: RewardTokenAdditionalInfoKeys.PlaceBetSignPostingData,
                value: JSON.stringify(placeBetSignPostingData),
                valueType: AdditionalInformationValueType.String,
            },
            {
                key: RewardTokenAdditionalInfoKeys.PlaceBetSignPostingVersion,
                value: this.tokensConfig.tokens.placeBetSignPostingVersion.toString(),
                valueType: AdditionalInformationValueType.Int8,
            },
        ];

        return additionalInfo;
    }

    private getIncrementalDetails(token: IRewardToken, payout: number, stake?: number): IncrementalPayoutDetails | undefined {
        if (payout <= 0 || !isIncrementalPayoutEligible(token, stake)) {
            return;
        }

        return {
            slabAmount: round(payout / token.incrementalPayoutNoOfTimes!, this.PRECISION),
            noOfSlabs: token.incrementalPayoutNoOfTimes,
        };
    }
}
